
import { useEffect, useState } from 'react'
import { Flame } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { getClosestRatio } from '../lib/get-closest-radio'
import type { Engine } from '../types/type'

interface SceneCardProps {
  engine: Engine
  onClick: () => void
}

export function SceneCard({ engine, onClick }: SceneCardProps) {
  const defaultCover = './default-cover/default.jpg'
  const displayUrl = engine.coverURL || engine.imageUrl || defaultCover
  const [aspectRatio, setAspectRatio] = useState(engine.aspectRatio || 3 / 4)

  const title = engine.title

  useEffect(() => {
    if (!displayUrl) return

    // 如果是用默认图，强制 3:4 比例
    if (displayUrl === defaultCover) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- 封面变为默认图时重置宽高比，行为符合预期
      setAspectRatio(3 / 4)
      return
    }

    const img = new Image()
    img.src = displayUrl
    img.onload = () => {
      const naturalRatio = img.naturalWidth / img.naturalHeight
      setAspectRatio(getClosestRatio(naturalRatio))
    }
  }, [displayUrl])

  const plays = 'statistics' in engine ? engine.statistics?.plays : 0

  // 判断是否为横向卡片（宽高比 > 1）
  const isLandscape = aspectRatio > 1

  return (
    <Card
      className='group relative h-fit cursor-pointer overflow-hidden rounded-2xl border-none p-0 shadow-lg transition-all hover:translate-y-[-4px] hover:shadow-xl'
      style={{ aspectRatio }}
      onClick={onClick}
    >
      {/* Background Image */}
      <img
        src={displayUrl}
        alt={title}
        className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-110'
        loading='lazy'
      />

      {/* Overlays */}
      <div className='absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent opacity-80' />
      <div className='absolute inset-0 bg-black/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />

      {/* Author Overlay - Top Left */}
      <div
        className={`absolute left-3 flex items-center gap-2 rounded-full bg-black/25 text-white backdrop-blur-md transition-colors hover:bg-black/40 ${isLandscape ? 'top-2 px-2 py-0.5 text-[10px]' : 'top-3 px-2.5 py-1 text-xs'
          }`}
      >
        <span className='font-medium opacity-90'>@{engine.author}</span>
      </div>

      {/* Content - Bottom */}
      <div
        className={`absolute bottom-0 left-0 w-full text-white ${isLandscape ? 'p-2.5' : 'p-4'
          }`}
      >
        <div className={`flex flex-col ${isLandscape ? 'gap-0.5' : 'gap-1'}`}>
          <h3
            className={`line-clamp-1 font-bold tracking-wide ${isLandscape ? 'text-base' : 'text-lg'
              }`}
          >
            {title}
          </h3>

          {/* 仅在非横向卡片时显示描述 */}
          {!isLandscape && (
            <p className='line-clamp-1 text-xs font-light text-white/80'>
              {engine.description}
            </p>
          )}

          {/* Tags and Likes Shared Container */}
          <div
            className={`flex items-center justify-between gap-2 ${isLandscape ? 'mt-0.5' : 'mt-1'
              }`}
          >
            {/* Tags Box */}
            <div
              className={`flex min-w-0 flex-1 items-center gap-1 overflow-hidden whitespace-nowrap text-white/50 ${isLandscape ? 'text-[9px]' : 'text-[10px]'
                }`}
            >
              {engine.category && (
                <>
                  <span className='shrink-0'>{engine.category}</span>
                  {engine.tags && engine.tags.length > 0 && (
                    <span className='shrink-0 opacity-40'>|</span>
                  )}
                </>
              )}
              <div className='flex min-w-0 items-center gap-1 overflow-hidden'>
                {engine.tags?.map((tag, index) => (
                  <span key={index} className='shrink-0'>
                    {tag}
                    {index < engine.tags.length - 1 && (
                      <span className='mx-0.5 opacity-40'>|</span>
                    )}
                  </span>
                ))}
              </div>
            </div>

            {/* Likes Box */}
            <div
              className={`flex shrink-0 items-center gap-1 rounded-full bg-white/10 text-white/90 backdrop-blur-md transition-colors hover:bg-white/20 ${isLandscape ? 'px-1.5 py-0.5' : 'px-2 py-0.5'
                }`}
            >
              <Flame
                className={`fill-white/10 transition-transform group-hover:scale-110 group-hover:fill-red-500 group-hover:text-red-500 ${isLandscape ? 'h-2.5 w-2.5' : 'h-3 w-3'
                  }`}
              />
              <span
                className={`font-medium ${isLandscape ? 'text-[9px]' : 'text-[10px]'}`}
              >
                {plays}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export function SceneCardSkeleton() {
  return (
    <Card className='bg-card relative h-fit overflow-hidden rounded-2xl border-none p-0 shadow-lg'>
      <div className='bg-muted/50 aspect-3/4 w-full'>
        {/* Author Badge Placeholder */}
        <div className='bg-background/40 absolute top-3 left-3 h-6 w-24 rounded-full backdrop-blur-md' />

        {/* Overlays Placeholder */}
        <div className='absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent' />

        {/* Content - Bottom */}
        <div className='absolute bottom-0 left-0 w-full space-y-3 p-4'>
          <div className='space-y-2'>
            {/* Title Placeholder */}
            <div className='h-5 w-2/3 rounded-md bg-white/20' />
            {/* Description Placeholder */}
            <div className='h-3 w-full rounded-md bg-white/10' />
          </div>

          {/* Footer - Three Columns Row */}
          <div className='flex items-center justify-between gap-3'>
            {/* Tags Column */}
            <div className='flex flex-1 items-center gap-1.5'>
              <div className='h-3 w-12 rounded-md bg-white/10' />
              <div className='h-3 w-1 rounded-sm bg-white/5' />
              <div className='h-3 w-16 rounded-md bg-white/10' />
            </div>

            {/* Likes Column */}
            <div className='h-5 w-12 rounded-full bg-white/10 backdrop-blur-md' />
          </div>
        </div>
      </div>
    </Card>
  )
}
